import { paymentPromiseListQuerySchema, type Page, type PaymentPromiseListQuery } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { useListQuery } from '../../../../../shared/hooks/use-list-query';
import { toListParams } from '../../../../../shared/policies/list-params';
import { ApiError } from '../../../../../shared/shared.api';
import { listPaymentPromises, type PaymentPromiseView } from '../payment-promises.api';

export type PaymentPromisesState =
  { status: 'loading' } | { status: 'loaded'; page: Page<PaymentPromiseView>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: PaymentPromiseListQuery) => toListParams(query).toString();

async function loadPromises(customerId: string, query: PaymentPromiseListQuery): Promise<PaymentPromisesState> {
  try {
    return { status: 'loaded', page: await listPaymentPromises(customerId, query), queryKey: keyOf(query) };
  } catch (err) {
    return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
  }
}

/** A customer's promises to pay, a page at a time from the server, with the query in the URL. `reload` refetches after a save. */
export function usePaymentPromises(customerId: string) {
  const list = useListQuery(paymentPromiseListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<PaymentPromisesState>({ status: 'loading' });
  const fetchPromises = useCallback(() => loadPromises(customerId, query), [customerId, query]);

  useEffect(() => {
    let cancelled = false;
    fetchPromises().then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchPromises]);

  return {
    state,
    list,
    isRefreshing: state.status === 'loaded' && state.queryKey !== keyOf(query),
    reload: async () => setState(await fetchPromises()),
  };
}
